import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {onClickHandler} from '../../../utils/onclick';
import iconDown from '../../../assets/images/icons/settings/down.png';
import iconUp from '../../../assets/images/icons/settings/up.png';
import './index.scss';
import {useSearchTerm} from '../../../hooks/search-term';

const typedMemo: <T>(c: T) => T = memo;

export type ColumnModel = {
    length: number;
    name: string;
    key: string;
};

type SortType = { column: ColumnModel; direction: 'ASC' | 'DES' };

type ListItemProps<T> = {
    index?: number;
    columns: ColumnModel[];
    item?: T;
    selected?: boolean;
    displayNameSelector: (item: T, column: ColumnModel, index?: number) => string;
    onClick?: () => void;
    className?: string;
    sort?: SortType;
    onColumnClick?: (column: ColumnModel) => void;
};

const ListItem = typedMemo(function ListItem<T>(props: ListItemProps<T>) {
    const {
        columns, selected, onClick, className,
        displayNameSelector, item, index, sort,
        onColumnClick,
    } = props;

    return (
        <div className={'line ' + className + (selected ? ' selected' : '')} {...onClickHandler(onClick)}>
            {/*<span>{index === -1 ? 'Sıra' : (index == null ? '-' : index + 1)}</span>*/}
            {
                columns.map((column, i) => (
                    <span
                        style={{flex: `${column.length} 1`}}
                        {...onClickHandler(sort ? () => onColumnClick(column) : null)}
                        key={`row_${i}`}
                    >
                        {displayNameSelector(item, column, index)}
                        {
                            renderIf(sort && sort.column === column && sort.direction)(() =>
                                renderIf(sort.direction === 'ASC')(
                                    () => <img className="small" src={iconUp} alt="up"/>,
                                    () => <img className="small" src={iconDown} alt="down"/>,
                                ))
                        }
                    </span>
                ))
            }
        </div>
    );
});

export type SettingsListProps<T> = {
    columns: ColumnModel[];
    items: T[];
    selectedItem: T;
    onItemSelected: (item: T) => void;
    keyExtractor: (item: Partial<T>) => string;
    classExtractor?: (item: Partial<T>) => string;
    displayNameExtractor?: (item: T, column: ColumnModel, index?: number) => string;
    notSelectableItems?: Partial<T>[];
    sortDisabled?: boolean;
};

const headerSelector = (item: any, column: ColumnModel) => column.name;

export const SettingsList = typedMemo(
    function SettingsList<T>(props: SettingsListProps<T>) {
        const {
            columns, items, selectedItem, onItemSelected,
            keyExtractor, classExtractor, notSelectableItems,
            displayNameExtractor, sortDisabled,
        } = props;

        const searchTerm = useSearchTerm();

        const [sort, setSort] = useState<SortType>({column: columns[0], direction: 'ASC'});

        const onColumnClick = useCallback((column: ColumnModel) => {
            if (sortDisabled) {
                return;
            }

            if (sort.column !== column) {
                setSort({column: column, direction: 'ASC'});
                return;
            }

            setSort({
                ...sort,
                direction: sort.direction === 'ASC' ? 'DES' : 'ASC',
            });
        }, [sort, sortDisabled]);

        const displayNameSelector = useMemo(() => {
            if (displayNameExtractor) {
                return displayNameExtractor;
            }

            return (item: T, column: ColumnModel) => {
                return item[column.key] || '-';
            };
        }, [displayNameExtractor]);

        const filteredSortedItems = useMemo(() => {
            if (!sort) {
                return items;
            }

            const diff = sort.direction === 'ASC' ? 1 : -1;
            return [...items]
                .filter(item => searchTerm == null ? true :
                    columns.reduce((acc: string, cur: ColumnModel) =>
                        `${acc} ${displayNameSelector(item, cur)}`, '').toLowerCase().includes(searchTerm.toLowerCase()))
                .sort(((a, b) => displayNameSelector(a, sort.column).toString().localeCompare(displayNameSelector(b, sort.column), 'tr', {numeric: true}) * diff));
        }, [columns, displayNameSelector, items, searchTerm, sort]);

        useEffect(() => {
            if (sortDisabled) {
                setSort(undefined);
            }
        }, [sortDisabled]);

        return (
            <div className="settings-list-component">
                <ListItem
                    index={-1}
                    columns={columns}
                    displayNameSelector={headerSelector}
                    sort={sort}
                    onColumnClick={onColumnClick}
                />
                {
                    renderIf(notSelectableItems)(() => notSelectableItems.map(item => (
                        <ListItem key={keyExtractor(item)} columns={columns} item={item}
                                  displayNameSelector={displayNameSelector}/>
                    )))
                }
                {
                    filteredSortedItems.map((item, index) => (
                        <ListItem
                            index={index}
                            key={`row_${keyExtractor(item)}`}
                            columns={columns}
                            item={item}
                            className={classExtractor ? classExtractor(item) : ''}
                            selected={selectedItem === item}
                            onClick={() => onItemSelected(item)}
                            displayNameSelector={displayNameSelector}
                        />
                    ))
                }
            </div>
        );
    },
);
